import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'

const serviceLinks = {
  'Massage therapy': '/services/massage-therapy',
  'TMJ care': '/services/tmj-treatment',
  Acupuncture: '/services/acupuncture',
  'Athletic therapy': '/services/athletic-therapy',
  'Sport massage': '/services/sports-massage',
  'Naturopathic medicine': '/services/naturopathic-medicine',
}

const cards = [
  {
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.05 4.575a1.575 1.575 0 10-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 013.15 0v1.5m-3.15 0l.075 5.925m3.075.75V4.575m0 0a1.575 1.575 0 013.15 0V15M6.9 7.575a1.575 1.575 0 10-3.15 0v8.175a6.75 6.75 0 006.75 6.75h2.018a5.25 5.25 0 003.712-1.538l1.732-1.732a5.25 5.25 0 001.538-3.712l.003-2.024a.668.668 0 01.198-.471 1.575 1.575 0 10-2.228-2.228 3.818 3.818 0 00-1.12 2.687M6.9 7.575V12m6.27 4.318A4.49 4.49 0 0116.35 15m.002 0h-.002" />
      </svg>
    ),
    title: 'Hands-On Care',
    description:
      'Personalized manual therapy to ease pain, reduce tension, and support how your body feels and moves.',
    services: ['Massage therapy', 'TMJ care', 'Acupuncture', 'Cupping'],
  },
  {
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
      </svg>
    ),
    title: 'Movement & Rehabilitation',
    description:
      'Guided recovery and movement support to help you build confidence in your body over time.',
    services: ['Athletic therapy', 'Sport massage', 'Corrective exercise'],
  },
  {
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
      </svg>
    ),
    title: 'Whole-Body Support',
    description:
      'Thoughtful care that considers your health, lifestyle, stress, and recovery as part of the bigger picture.',
    services: ['Naturopathic medicine', 'Relaxation massage', 'Acupuncture'],
  },
]

const HowWeCare = () => {
  return (
    <section className="relative pt-6 pb-16 md:pt-10 md:pb-24">
      <div className="relative z-[2] max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <motion.h2
          className="font-display text-cream-200 text-3xl md:text-4xl lg:text-5xl text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          How We Care for Your Body
        </motion.h2>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {cards.map((card, index) => (
            <motion.div
              key={card.title}
              className="bg-cream-200 rounded-[20px] p-8 md:p-10 flex flex-col"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-30px' }}
              transition={{
                duration: 0.6,
                delay: index * 0.15,
                ease: 'easeOut',
              }}
            >
              {/* Serif title */}
              <h3 className="font-display text-brand-orange-700 text-xl md:text-2xl font-bold mb-4">
                {card.title}
              </h3>

              {/* Services list — pill tags so they stand out */}
              <div className="flex flex-wrap gap-2 mb-5">
                {card.services.map((service) => {
                  const pillClass =
                    'inline-block bg-brand-orange-500/10 text-brand-orange-700 rounded-full px-3 py-1 font-sans text-xs font-semibold tracking-wide'
                  return serviceLinks[service] ? (
                    <Link
                      key={service}
                      to={serviceLinks[service]}
                      className={`${pillClass} hover:bg-brand-orange-500 hover:text-white transition-colors`}
                    >
                      {service}
                    </Link>
                  ) : (
                    <span key={service} className={pillClass}>
                      {service}
                    </span>
                  )
                })}
              </div>

              {/* Body text */}
              <p className="font-sans text-brand-orange-600 text-base leading-relaxed">
                {card.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default HowWeCare
